/**
 * Full per-chart-type geometry: pure functions that take acoustic data in and
 * return a renderer-agnostic scene description out (grid ticks, plot-local
 * point paths, y-axis ticks, target bands). Renderers (Canvas2D, Skia) paint
 * this data however they like — see `draw.ts` for a Canvas2D convenience
 * layer built on top of it.
 *
 * Every field is plain data (numbers, plot-local px) — no color, font, or
 * platform-specific type ever appears here. Caption/label *strings* beyond
 * axis tick text (e.g. "peak 0.34s @ 143 Hz") are product copy and stay
 * app-owned; this module exposes the raw numbers (`peak`, `nearestValueAtFreq`)
 * a caller needs to build them.
 */

import { canvasType } from '@roomtreatment/design';
import { inflatePadForYAxis } from './axisMargin';
import { createPlotFrame, DEFAULT_CHART_PAD, type ChartPad, type PlotFrame } from './layout';
import {
  createLinearScale,
  createLogFreqScale,
  linearToPlot,
  logFreqToX,
  type LinearScale,
  type LogFreqScale,
} from './scales';
import {
  alphaViewportFromSeries,
  dbViewportFromSeries,
  magToDb,
  mapSeriesToPlot,
  shiftDbToMean,
  type PlotPoint,
} from './series';
import { computeLogTicks, linearTicks, niceLinearTicks, tickPositions, placeTickLabels, type LabeledTick, type PositionedTick } from './ticks';

export interface AxisTicks {
  /** Every tick (major + minor), positioned — draw a gridline for each. */
  all: PositionedTick[];
  /** The subset that should carry a text label (collision-avoided). */
  labeled: LabeledTick[];
}

export interface YTick {
  value: number;
  y: number;
  major: boolean;
  label: string;
}

export function defaultMeasureWidth(label: string): number {
  // ~0.55em average glyph width at the canvas scale's tick size (11px,
  // `canvasType.tick` in @roomtreatment/design) — used when a renderer can't
  // measure real text (Skia without a loaded font). Callers that have a real
  // measurer (Canvas2D `ctx.measureText`, a Skia `SkFont`) should pass it for
  // pixel-accurate collision avoidance.
  return label.length * canvasType.tick.size * 0.55;
}

export function computeXAxisTicks(
  fMin: number,
  fMax: number,
  xScale: LogFreqScale,
  measureWidth: (label: string) => number,
): AxisTicks {
  const all = tickPositions(computeLogTicks(fMin, fMax), (f) => logFreqToX(xScale, f));
  const labeled = placeTickLabels(all, measureWidth);
  return { all, labeled };
}

// ── Frequency response ────────────────────────────────────────────────────

export interface FreqResponseInput {
  freqs: number[];
  mag: number[];
  magBaseline?: number[] | null;
  width: number;
  height: number;
  pad?: ChartPad;
  fMin?: number;
  fMax?: number;
  measureWidth?: (label: string) => number;
  formatYLabel?: (db: number) => string;
}

export interface FreqResponseGeometry {
  empty: boolean;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  xScale: LogFreqScale;
  yScale: LinearScale;
  yDomain: { lo: number; hi: number };
  xTicks: AxisTicks;
  yTicks: YTick[];
  activeDb: number[];
  baselineDb: number[] | null;
  activePoints: PlotPoint[];
  baselinePoints: PlotPoint[] | null;
}

export function computeFreqResponseGeometry(opts: FreqResponseInput): FreqResponseGeometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 20;
  const fMax = opts.fMax ?? 200;
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const formatYLabel = opts.formatYLabel ?? ((v: number) => `${Math.round(v)}`);

  if (!opts.freqs.length || !opts.mag.length || frame.plotW <= 0) {
    return {
      empty: true, frame, fMin, fMax, xScale,
      yScale: createLinearScale(frame.plotH, 0, 15, true),
      yDomain: { lo: 0, hi: 15 },
      xTicks: computeXAxisTicks(fMin, fMax, xScale, measureWidth),
      yTicks: [], activeDb: [], baselineDb: null, activePoints: [], baselinePoints: null,
    };
  }

  const rawDb = magToDb(opts.mag);
  const baseRaw = opts.magBaseline?.length ? magToDb(opts.magBaseline) : null;
  const ref = baseRaw ?? rawDb;
  const activeDb = shiftDbToMean(rawDb, ref);
  const baselineDb = baseRaw ? shiftDbToMean(baseRaw, ref) : null;

  const yDomain = dbViewportFromSeries(baselineDb ? [activeDb, baselineDb] : [activeDb]);
  const yScale = createLinearScale(frame.plotH, yDomain.lo, yDomain.hi, true);

  const activePoints = mapSeriesToPlot({ x: opts.freqs, y: activeDb }, xScale, yScale);
  const baselinePoints = baselineDb
    ? mapSeriesToPlot({ x: opts.freqs, y: baselineDb }, xScale, yScale)
    : null;

  const { values: yTickVals, majorInterval } = niceLinearTicks(yDomain.lo, yDomain.hi, 8);
  const yTicks: YTick[] = yTickVals.map((v) => ({
    value: v,
    y: linearToPlot(yScale, v),
    major: Math.round(v) % majorInterval === 0,
    label: formatYLabel(v),
  }));

  return {
    empty: false, frame, fMin, fMax, xScale, yScale, yDomain,
    xTicks: computeXAxisTicks(fMin, fMax, xScale, measureWidth),
    yTicks, activeDb, baselineDb, activePoints, baselinePoints,
  };
}

// ── RT60 ───────────────────────────────────────────────────────────────────

export interface Rt60Input {
  freqs: number[];
  values: number[];
  baselineFreqs?: number[] | null;
  baselineValues?: number[] | null;
  width: number;
  height: number;
  pad?: ChartPad;
  fMin?: number;
  fMax?: number;
  /** Studio target band, in seconds. Pass null to disable. Default 0.2–0.4s. */
  targetBand?: [number, number] | null;
  measureWidth?: (label: string) => number;
  formatYLabel?: (seconds: number) => string;
}

export interface Rt60Geometry {
  empty: boolean;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  xScale: LogFreqScale;
  yScale: LinearScale;
  maxVal: number;
  xTicks: AxisTicks;
  yTicks: YTick[];
  targetBandY: { y0: number; y1: number } | null;
  activePoints: PlotPoint[];
  baselinePoints: PlotPoint[] | null;
  /** False when the baseline is absent, or pointwise identical to the active
   *  curve (no point drawing a perfectly overlapping second line). */
  hasBaseline: boolean;
}

export function computeRt60Geometry(opts: Rt60Input): Rt60Geometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 20;
  const fMax = opts.fMax ?? 200;
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const formatYLabel = opts.formatYLabel ?? ((v: number) => v.toFixed(2));
  const targetBand = opts.targetBand === undefined ? [0.2, 0.4] as [number, number] : opts.targetBand;

  const hasBaselineInput = !!opts.baselineFreqs?.length && !!opts.baselineValues?.length;
  const baselineIdentical =
    hasBaselineInput
    && opts.baselineFreqs!.length === opts.freqs.length
    && opts.baselineValues!.every((v, i) => Math.abs(v - opts.values[i]!) <= 1e-3);
  const hasBaseline = hasBaselineInput && !baselineIdentical;

  if (!opts.freqs.length || frame.plotW <= 0) {
    return {
      empty: true, frame, fMin, fMax, xScale,
      yScale: createLinearScale(frame.plotH, 0, 1, true),
      maxVal: 1,
      xTicks: computeXAxisTicks(fMin, fMax, xScale, measureWidth),
      yTicks: [], targetBandY: null, activePoints: [], baselinePoints: null, hasBaseline: false,
    };
  }

  const allVals = (hasBaseline ? [...opts.values, ...opts.baselineValues!] : opts.values)
    .filter(Number.isFinite);
  const hi = allVals.length ? Math.max(...allVals) : 0;
  const maxVal = Math.max(1.0, Math.ceil(Math.max(hi, 0.5) * 1.1 * 10) / 10);
  const yScale = createLinearScale(frame.plotH, 0, maxVal, true);

  const activePoints = mapSeriesToPlot({ x: opts.freqs, y: opts.values }, xScale, yScale);
  const baselinePoints = hasBaseline
    ? mapSeriesToPlot({ x: opts.baselineFreqs!, y: opts.baselineValues! }, xScale, yScale)
    : null;

  const step = maxVal > 2 ? 0.5 : maxVal > 1 ? 0.25 : 0.1;
  const yTicks: YTick[] = linearTicks(0, maxVal, step).map((v) => ({
    value: v, y: linearToPlot(yScale, v), major: false, label: formatYLabel(v),
  }));

  const targetBandY = targetBand
    ? { y0: linearToPlot(yScale, targetBand[1]), y1: linearToPlot(yScale, targetBand[0]) }
    : null;

  return {
    empty: false, frame, fMin, fMax, xScale, yScale, maxVal,
    xTicks: computeXAxisTicks(fMin, fMax, xScale, measureWidth),
    yTicks, targetBandY, activePoints, baselinePoints, hasBaseline,
  };
}

// ── Absorption coefficient ──────────────────────────────────────────────────

export interface AbsorptionInput {
  freqs: number[];
  alpha: number[];
  width: number;
  height: number;
  pad?: ChartPad;
  /** Omit to auto-fit the axis to the actual data extent (e.g. a curve
   *  truncated to a solver's solved range) — pass explicitly only to pin a
   *  fixed axis regardless of what the data covers. */
  fMin?: number;
  fMax?: number;
  measureWidth?: (label: string) => number;
}

export interface AbsorptionGeometry {
  empty: boolean;
  /** Effective pad after y-axis label collision avoidance. */
  pad: ChartPad;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  xScale: LogFreqScale;
  yScale: LinearScale;
  yDomain: { lo: number; hi: number };
  xTicks: AxisTicks;
  yTicks: YTick[];
  points: PlotPoint[];
  peak: { alpha: number; freq: number } | null;
}

export function computeAbsorptionGeometry(opts: AbsorptionInput): AbsorptionGeometry {
  const basePad = opts.pad ?? DEFAULT_CHART_PAD;
  const finiteFreqs = opts.freqs.filter(Number.isFinite);
  // Auto-fit the x-axis to the real data extent when the caller doesn't pin
  // one — a curve truncated to a solver's solved range (e.g. the panel
  // absorption chart above its modal fmax) should shrink the axis with it,
  // not trail off against a fixed 40Hz–10kHz frame it no longer fills.
  const dataFMin = finiteFreqs.length ? Math.min(...finiteFreqs) : 20;
  const dataFMax = finiteFreqs.length ? Math.max(...finiteFreqs) : 20000;
  const fMin = opts.fMin ?? dataFMin;
  const fMax = opts.fMax ?? Math.max(dataFMax, fMin * 1.001);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;

  const emptyResult = (pad: ChartPad, frame: PlotFrame, xScale: LogFreqScale): AbsorptionGeometry => ({
    empty: true,
    pad,
    frame,
    fMin,
    fMax,
    xScale,
    yScale: createLinearScale(frame.plotH, 0, 1, true),
    yDomain: { lo: 0, hi: 1 },
    xTicks: computeXAxisTicks(fMin, fMax, xScale, measureWidth),
    yTicks: [],
    points: [],
    peak: null,
  });

  if (!opts.freqs.length) {
    const frame = createPlotFrame(opts.width, opts.height, basePad);
    const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
    return emptyResult(basePad, frame, xScale);
  }

  // Auto-fit the y-axis to the data too — a panel absorbing 0.6-0.8 across
  // the board shouldn't get squashed into the top fifth of a fixed 0-1
  // axis; see alphaViewportFromSeries for why 0 isn't a forced floor.
  const yDomain = alphaViewportFromSeries([opts.alpha]);
  const span = yDomain.hi - yDomain.lo;
  const step = span > 0.6 ? 0.2 : span > 0.3 ? 0.1 : 0.05;
  const yTickLabels = linearTicks(yDomain.lo, yDomain.hi, step).map((v) => v.toFixed(2));
  const pad = inflatePadForYAxis(basePad, yTickLabels, measureWidth);

  const frame = createPlotFrame(opts.width, opts.height, pad);
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  if (frame.plotW <= 0) return emptyResult(pad, frame, xScale);

  const yScale = createLinearScale(frame.plotH, yDomain.lo, yDomain.hi, true);
  const points = mapSeriesToPlot({ x: opts.freqs, y: opts.alpha }, xScale, yScale);
  const yTicks: YTick[] = linearTicks(yDomain.lo, yDomain.hi, step).map((v) => ({
    value: v, y: linearToPlot(yScale, v), major: true, label: v.toFixed(2),
  }));

  let peak: { alpha: number; freq: number } | null = null;
  opts.freqs.forEach((f, i) => {
    const a = opts.alpha[i]!;
    if (Number.isFinite(a) && (!peak || a > peak.alpha)) peak = { alpha: a, freq: f };
  });

  return {
    empty: false,
    pad,
    frame,
    fMin,
    fMax,
    xScale,
    yScale,
    yDomain,
    xTicks: computeXAxisTicks(fMin, fMax, xScale, measureWidth),
    yTicks,
    points,
    peak,
  };
}
